import { useEffect, type ReactNode } from "react";
import {
  Dribbble,
  Facebook,
  Github,
  Globe2,
  Instagram,
  Linkedin,
  Mail,
  MessageCircle,
  Send,
  Twitter,
  Youtube,
  type LucideIcon,
} from "lucide-react";
import { Button, Modal, Skeleton } from "@/components/ui";
import type { CmsSocialPlatform } from "../types";

export function CmsLoadError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="cms-load-error">
      <p>{message}</p>
      <Button variant="primary" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

export function CmsPageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="cms-header">
      <div>
        <h2 className="cms-header__title">{title}</h2>
        <p className="cms-header__desc">{description}</p>
      </div>
      {actions ? <div className="cms-header__actions">{actions}</div> : null}
    </header>
  );
}

export function CmsKpiCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <article className="cms-kpi">
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <em>{hint}</em> : null}
    </article>
  );
}

export function CmsSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="cms-skeleton">
      <Skeleton height={32} width="28%" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} height={i === 0 ? 120 : 72} />
      ))}
    </div>
  );
}

export function CmsPanel({
  title,
  description,
  actions,
  children,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="cms-panel">
      {title ? (
        <header className="cms-panel__head">
          <div>
            <h3>{title}</h3>
            {description ? <p>{description}</p> : null}
          </div>
          {actions ? <div className="cms-panel__actions">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function CmsEmpty({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="cms-empty">
      <strong>{title}</strong>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal open={open} title={title} onClose={onClose}>
      <p className="cms-confirm__msg">{message}</p>
      <div className="cms-confirm__actions">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={onConfirm}>{confirmLabel}</Button>
      </div>
    </Modal>
  );
}

export function useUnsavedChanges(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
}

const SOCIAL_ICONS: Record<CmsSocialPlatform, LucideIcon> = {
  linkedin: Linkedin,
  instagram: Instagram,
  facebook: Facebook,
  x: Twitter,
  youtube: Youtube,
  github: Github,
  behance: Globe2,
  dribbble: Dribbble,
  whatsapp: MessageCircle,
  telegram: Send,
  email: Mail,
};

const SOCIAL_LABELS: Record<CmsSocialPlatform, string> = {
  linkedin: "LinkedIn",
  instagram: "Instagram",
  facebook: "Facebook",
  x: "X (Twitter)",
  youtube: "YouTube",
  github: "GitHub",
  behance: "Behance",
  dribbble: "Dribbble",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  email: "Email",
};

export function CmsSocialIcon({ platform, size = 18 }: { platform: CmsSocialPlatform; size?: number }) {
  const Icon = SOCIAL_ICONS[platform];
  return <Icon size={size} aria-hidden />;
}

export function cmsSocialLabel(platform: CmsSocialPlatform | "custom", customName?: string | null) {
  if (platform === "custom" && customName) return customName;
  return SOCIAL_LABELS[platform as CmsSocialPlatform] ?? customName ?? "Custom";
}

export const CMS_SOCIAL_PLATFORMS: CmsSocialPlatform[] = [
  "linkedin",
  "instagram",
  "facebook",
  "x",
  "youtube",
  "github",
  "behance",
  "dribbble",
  "whatsapp",
  "telegram",
  "email",
];

export function formatCmsDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatCmsDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function mockFileUrl(file: File) {
  return URL.createObjectURL(file);
}